import { useMemo, useState } from 'react';
import styles from './ProfileSelector.module.css';
import { Popover } from './ui/Popover';
import type { Profile } from '@/lib/auth';
import arrowDown16 from '@/assets/icons/arrow-down-16.svg';

/**
 * The brand this dashboard is scoped to — the header's Targeting 360 profile
 * pill, against the list the sign-in actually returned.
 *
 * The brand used to be a BUILD-TIME constant (VITE_CUSTOMER_NAME), so one
 * bundle served exactly one tenant and a user with 32 grants could see one of
 * them. The list here is the same one the API's tenant guard enforces, so the
 * picker cannot offer a brand a request would then be refused for.
 */
export interface ProfileSelectorProps {
  profiles: Profile[];
  active: string | null;
  onSelect: (name: string) => void;
}

/** Initials for the avatar: "Nobel Hygiene" -> "NH", "crocon" -> "CR". */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
}

export function ProfileSelector({ profiles, active, onSelect }: ProfileSelectorProps) {
  const [search, setSearch] = useState('');

  const current = useMemo(
    () => profiles.find((p) => p.name === active) ?? null,
    [profiles, active],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? profiles.filter((p) => p.name.toLowerCase().includes(q)) : profiles;
    // Alphabetical, case-insensitive: the upstream order is by profileId, which
    // is arbitrary to a reader hunting for one of 32 names.
    return [...rows].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [profiles, search]);

  return (
    <Popover
      align="right"
      trigger={({ toggle }) => (
        <button
          type="button"
          className={styles.pill}
          onClick={toggle}
          aria-haspopup="listbox"
          aria-label={`Profile: ${current?.name ?? 'none selected'}`}
          disabled={profiles.length === 0}
        >
          <span className={styles.avatar} aria-hidden="true">
            {initials(current?.name ?? '?')}
          </span>
          <span className={styles.text}>
            <span className={styles.name}>{current?.name ?? 'Select profile'}</span>
            {/* Role comes from the platform grant. Rendered only when it is
                actually known — an empty "Role:" label is worse than none. */}
            {current?.role && <span className={styles.role}>Role: {current.role}</span>}
          </span>
          <img className={styles.chevron} src={arrowDown16} alt="" />
        </button>
      )}
    >
      {({ close }) => (
        <div className={styles.panel} role="listbox" aria-label="Profiles">
          <div className={styles.searchWrap}>
            <input
              className={styles.search}
              placeholder="Search profiles…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              autoFocus
            />
          </div>
          <p className={styles.count}>
            {filtered.length} of {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'}
          </p>
          <div className={styles.list}>
            {filtered.length === 0 && <p className={styles.empty}>No profile matches “{search}”.</p>}
            {filtered.map((p) => (
              <button
                key={p.slug ?? `${String(p.id)}-${p.name}`}
                type="button"
                role="option"
                aria-selected={p.name === active}
                className={`${styles.option} ${p.name === active ? styles.on : ''}`}
                onClick={() => { onSelect(p.name); setSearch(''); close(); }}
              >
                <span className={styles.optionAvatar} aria-hidden="true">{initials(p.name)}</span>
                <span className={styles.optionText}>
                  <span className={styles.optionName}>{p.name}</span>
                  {p.role && <span className={styles.optionRole}>{p.role}</span>}
                </span>
                {p.name === active && <span className={styles.tick}>✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}
